import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faInfoCircle,
  faTimes,
  faEnvelope,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { BASE_URL } from "../config";
import { ResizableBox } from "react-resizable";
import * as XLSX from "xlsx";
import "react-resizable/css/styles.css";

const MissingCorpPrefixAlert = ({ onClose }) => {
  const [corporates, setCorporates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMissingCorpPrefix = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/missing-corp-prefix`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        setCorporates(response.data.data.corporates);
        setLoading(false);
      } catch (err) {
        setError("Failed to load corporate records. Please try again.");
        setLoading(false);
      }
    };

    fetchMissingCorpPrefix();
  }, []);

  const handleNotifyIT = () => {
    const subject = encodeURIComponent("Urgent: Missing Corporate Prefix Alert");
    const body = encodeURIComponent(
      `The Billing and Collection system has detected corporate records with missing corp_prefix values, which will cause the Generate Billing functionality to fail in all modules. Please assign appropriate corp_prefix values to the following records before using Generate Billing:\n\n` +
      corporates
        .map((corp) => `Corporate Code: ${corp.corporateCode}, Name: ${corp.corporateName}`)
        .join("\n") +
      `\n\nPlease coordinate with the IT team to resolve this issue urgently.`
    );
    window.location.href = `mailto:dev@amaphil.com.ph?subject=${subject}&body=${body}`;
  };

  const handleExportXLSX = () => {
    const worksheetData = corporates.map((corp) => ({
      "Corporate Code": corp.corporateCode,
      "Corporate Name": corp.corporateName,
    }));
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "MissingCorpPrefix");
    XLSX.writeFile(workbook, "MissingCorpPrefixRecords.xlsx");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <ResizableBox
        width={700}
        height={550}
        minConstraints={[400, 400]}
        maxConstraints={[800, 800]}
        className="bg-white rounded-2xl shadow-xl mx-4 overflow-hidden border border-gray-100"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="bg-red-50 p-4 flex items-center justify-between rounded-t-2xl">
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className="text-red-600 text-2xl"
              />
              <h3 className="text-xl font-semibold text-red-600">
                Urgent: Missing Corporate Prefix Alert
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-lg" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 flex-1 overflow-y-auto">
            <p className="text-gray-700 mb-4">
              The Billing and Collection system has detected corporate records without a <strong>corp_prefix</strong>. <strong>Generate Billing will not work as intended</strong> due to these missing values. Please coordinate with the IT team to assign appropriate prefix values before using the Generate Billing functionality in any modules.
            </p>

            {/* Corporate List */}
            <div className="bg-gray-50 p-4 rounded-xl mb-4 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="text-blue-500 text-lg"
                />
                <h4 className="text-sm font-medium text-gray-800">
                  Affected Corporate Records
                </h4>
              </div>
              {loading ? (
                <p className="text-sm text-gray-700">Loading...</p>
              ) : error ? (
                <p className="text-sm text-red-600">{error}</p>
              ) : corporates.length === 0 ? (
                <p className="text-sm text-gray-700">No records found.</p>
              ) : (
                <ul className="space-y-2 max-h-48 overflow-y-auto">
                  {corporates.map((corp) => (
                    <li key={corp.id} className="text-sm text-gray-700">
                      <span className="font-medium">Code:</span> {corp.corporateCode} |{" "}
                      <span className="font-medium">Name:</span> {corp.corporateName}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={handleExportXLSX}
                disabled={loading || error || corporates.length === 0}
                className={`flex-1 py-2 px-4 rounded-xl transition-colors flex items-center justify-center ${
                  loading || error || corporates.length === 0
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-green-600 text-white hover:bg-green-700"
                }`}
              >
                <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                Export to XLSX
              </button>
              {/* <button
                onClick={handleNotifyIT}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-xl hover:bg-red-700 transition-colors flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                Notify IT Team
              </button> */}
              <button
                onClick={onClose}
                className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-xl hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </ResizableBox>
    </div>
  );
};

export default MissingCorpPrefixAlert;